import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import SEO from '../components/SEO';
import { BlogCardSkeleton } from '../components/SkeletonLoader';
import PageWrapper from '../components/PageWrapper';
import api from '../services/api';
import SafeImage from '../components/SafeImage';
import '../styles/pages/BlogPage.css';

const BlogPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // API State
  const [heroPost, setHeroPost] = useState(null);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [mainPosts, setMainPosts] = useState([]);
  const [gridPosts, setGridPosts] = useState([]);
  const [gridPage, setGridPage] = useState(1);
  const [hasMoreGrid, setHasMoreGrid] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  // Ref for intersection observer
  const observerTarget = useRef(null);

  const fetchCategories = async () => {
    try {
      const response = await api.getBlogCategories();
      if (response.success && response.data) {
        setCategories(response.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchBlogData();

    return () => {
      setLoading(true);
      setHeroPost(null);
      setFeaturedPosts([]);
      setMainPosts([]);
      setGridPosts([]);
      setGridPage(1);
      setHasMoreGrid(true);
    };
  }, [location.pathname]);

  // Setup intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !loadingMore && hasMoreGrid) {
          loadMoreGridPosts();
        }
      },
      { threshold: 0.1 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [loading, loadingMore, hasMoreGrid, gridPage]);

  const fetchBlogData = async () => {
    try {
      setLoading(true);

      // Fetch hero post (1 post with hero=1)
      const heroResponse = await api.getHeroBlogPost();
      if (heroResponse.success && heroResponse.data) {
        setHeroPost(heroResponse.data);
      }

      // Fetch featured posts (posts with featured=1)
      const featuredResponse = await api.getFeaturedBlogPosts();
      if (featuredResponse.success && featuredResponse.data) {
        setFeaturedPosts(featuredResponse.data);
      }

      // Fetch first 2 normal posts for main section (skip=0, limit=2)
      const mainResponse = await api.getBlogPosts({ page: 1, limit: 2, skip: 0 });
      if (mainResponse.success && mainResponse.data) {
        setMainPosts(mainResponse.data);
      }

      await fetchGridPosts(1);
    } catch (error) {
      console.error('Error fetching blog data:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchGridPosts = async (pageNum = 1, category = selectedCategory) => {
    try {
      if (pageNum === 1) {
        setLoading(true);
      }

      const response = await api.getBlogPosts({
        page: pageNum,
        limit: 12,
        skip: 0,
        category_id: category || null
      });

      if (response.success && response.data) {
        if (pageNum === 1) {
          setGridPosts(response.data);
        } else {
          setGridPosts(prev => {
            const existingIds = new Set(prev.map(p => p.id));
            const newPosts = response.data.filter(p => !existingIds.has(p.id));
            return [...prev, ...newPosts];
          });
        }
        setHasMoreGrid(response.pagination?.has_more || false);
        setGridPage(pageNum);
      }
    } catch (error) {
      console.error('Error fetching grid posts:', error);
    } finally {
      if (pageNum === 1) {
        setLoading(false);
      }
    }
  };

  const loadMoreGridPosts = async () => {
    if (!hasMoreGrid || loadingMore) return;

    try {
      setLoadingMore(true);
      await new Promise(resolve => setTimeout(resolve, 300));
      await fetchGridPosts(gridPage + 1);
    } catch (error) {
      console.error('Error loading more posts:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/blog/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setGridPosts([]);
    setGridPage(1);
    setHasMoreGrid(true);
    fetchGridPosts(1, category);
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subscribeEmail) {
      toast.error('Please enter your email');
      return;
    }
    if (!/^\S+@\S+$/i.test(subscribeEmail)) {
      toast.error('Invalid email address');
      return;
    }

    try {
      const response = await api.subscribeNewsletter(subscribeEmail);
      if (response.success) {
        toast.success('Successfully subscribed!');
        setSubscribeEmail('');
      }
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    }
  };

  return (
    <PageWrapper>
      <SEO
        title="Lab Notes - Latest Insights | Supernova Lab"
        description="Explore the latest insights, research, and stories from Supernova Lab"
        keywords="innovation, research, blog, lab notes, Barbados"
      />

      <main className="blog-page">
        {/* HEADER WITH SEARCH */}
        <motion.section
          className="blog-header-section"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="container">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <h1 className="blog-main-title mb-0">Lab Notes</h1>
              
              {/* Search Box */}
              <form onSubmit={handleSearch} className="blog-search-wrapper">
                <input
                  type="text"
                  className="blog-search-input"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </form>
            </div>
            <div className="blog-divider mt-4"></div>
          </div>
        </motion.section>

        {/* Hero Post Section */}
        <motion.section
          className="hero-post-section py-1"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <div className="container">
            {loading ? (
              <BlogCardSkeleton />
            ) : heroPost ? (
              <Link to={`/blog/${heroPost.slug}`} className="hero-post-link">
                <div className="hero-post-card">
                  <SafeImage
                    src={heroPost.image}
                    alt={heroPost.title}
                    className="hero-post-image"
                    width={1200}
                    height={600}
                    placeholderText="Featured Post"
                  />
                  <div className="hero-post-overlay">
                    <span className="hero-post-badge">{heroPost.category_name}</span>
                    <h2 className="hero-post-title">{heroPost.title}</h2>
                    <p className="hero-post-description">{heroPost.description}</p>
                  </div>
                </div>
              </Link>
            ) : (
              <div className="text-center text-muted py-5">
                <p>No hero post available</p>
              </div>
            )}
          </div>
        </motion.section>

        {/* Main Posts Section - First 2 Normal Posts */}
        <section className="main-posts-section">
          <div className="container">
            {loading ? (
              Array(2).fill(0).map((_, index) => (
                <div key={index} className="mb-5">
                  <BlogCardSkeleton />
                </div>
              ))
            ) : mainPosts.length > 0 ? (
              mainPosts.map((post, index) => {
                const imagePosition = index % 2 === 0 ? 'right' : 'left';
                const postDate = new Date(post.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                });

                return (
                  <motion.div
                    key={post.id}
                    initial={{ opacity: 0, x: imagePosition === 'right' ? -50 : 50 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                  >
                    <Link to={`/blog/${post.slug}`} className="main-post-link">
                      <div className="main-post-card">
                        <div className="row g-0 align-items-center">
                          {imagePosition === 'left' ? (
                            <>
                              <div className="col-lg-6">
                                <div className="main-post-image">
                                  <SafeImage
                                    src={post.image}
                                    alt={post.title}
                                    width={600}
                                    height={400}
                                    placeholderText="Blog Post"
                                  />
                                </div>
                              </div>
                              <div className="col-lg-6">
                                <div className="main-post-content mx-0 mx-lg-5">
                                  <span className="main-post-badge">{post.category_name}</span>
                                  <h3 className="main-post-title">{post.title}</h3>
                                  <p className="main-post-description">{post.description}</p>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.75rem' }}>
                                    <p className="main-post-date mb-0">{postDate}</p>
                                    <button className="main-post-btn">Read more</button>
                                  </div>
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="col-lg-6">
                                <div className="main-post-content pe-3 pe-lg-5">
                                  <span className="main-post-badge">{post.category_name}</span>
                                  <h3 className="main-post-title">{post.title}</h3>
                                  <p className="main-post-description">{post.description}</p>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.75rem' }}>
                                    <p className="main-post-date mb-0">{postDate}</p>
                                    <button className="main-post-btn">Read more</button>
                                  </div>
                                </div>
                              </div>
                              <div className="col-lg-6">
                                <div className="main-post-image">
                                  <SafeImage
                                    src={post.image}
                                    alt={post.title}
                                    width={600}
                                    height={400}
                                    placeholderText="Blog Post"
                                  />
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })
            ) : null}
          </div>
        </section>

        {/* Featured Articles Grid Section */}
        <section className="featured-articles-section">
          <div className="container">
            <div className="blog-divider"></div>

            {/* Category Filter */}
            <div className="featured-article-section py-4 py-lg-5">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                <h2 className="featured-articles-heading mb-0">All Articles</h2>

                <select
                  className="form-select whats-next-filter-btn"
                  value={selectedCategory}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  style={{ width: '200px', cursor: 'pointer' }}
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {loading && gridPosts.length === 0 ? (
              <div className="row g-4">
                {Array(8).fill(0).map((_, index) => (
                  <div key={index} className="col-lg-3 col-md-6">
                    <BlogCardSkeleton />
                  </div>
                ))}
              </div>
            ) : gridPosts.length > 0 ? (
              <>
                <div className="row g-4">
                  {gridPosts.map((article) => {
                    const articleDate = new Date(article.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    });

                    return (
                      <div key={article.id} className="col-lg-3 col-md-6">
                        <Link to={`/blog/${article.slug}`} className="article-card-link">
                          <div className="article-card">
                            <div className="article-card-image">
                              <SafeImage
                                src={article.image}
                                alt={article.title}
                                width={400}
                                height={250}
                                placeholderText="Article"
                              />
                            </div>
                            <div className="article-card-content">
                              <span className="article-card-category">{article.category_name}</span>
                              <h4 className="article-card-title">{article.title}</h4>
                              <p className="article-card-date">{articleDate}</p>
                            </div>
                          </div>
                        </Link>
                      </div>
                    );
                  })}
                </div>

                {/* Loading More Skeleton */}
                {loadingMore && (
                  <div className="row g-4 mt-1">
                    {Array(4).fill(0).map((_, index) => (
                      <div key={`loading-${index}`} className="col-lg-3 col-md-6">
                        <BlogCardSkeleton />
                      </div>
                    ))}
                  </div>
                )}

                {/* Intersection Observer Target */}
                <div ref={observerTarget} style={{ height: '20px' }} />
              </>
            ) : (
              <div className="row">
                <div className="col-12">
                  <div className="text-center text-muted py-5">
                    <p>No articles available at the moment.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="newsletter-section-alt">
          <div className="container">
            <div className="text-center">
              <h2 className="newsletter-title-alt mb-3">Stay Informed with Supernova</h2>
              <p className="newsletter-subtitle-alt mb-4">Subscribe to our newsletter for the latest updates.</p>
              <form onSubmit={handleSubscribe} className="newsletter-form-alt" style={{ width: '100%' }}>
                <input
                  type="email"
                  className="newsletter-input-alt"
                  placeholder="Enter your email"
                  value={subscribeEmail}
                  onChange={(e) => setSubscribeEmail(e.target.value)}
                />
                <button className="newsletter-btn-alt" type="submit">Subscribe</button>
              </form>
            </div>
          </div>
        </section>
      </main>
   </PageWrapper>
  );
};

export default BlogPage;